import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    CircularProgress,
    Alert,
    InputLabel,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    Save as SaveIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
// Removed MUI X Date Pickers imports to avoid dayjs plugin conflicts
// Removed dayjs imports to avoid plugin conflicts
import useFetch from '../../../../hooks/useFetch';
import useSubmitForm from '../../../../hooks/useSubmitForm';
import RTL from '../../../../utils/RTL';
import { toPersianDigits, toEnglishDigits } from '../../../../utils/convertNumbers';
import FloatingSnackbar from '../../../common/FloatingSnackbar';

// Helper function to ensure time format is HH:MM (no seconds)
const formatTimeWithoutSeconds = (timeStr) => {
    if (!timeStr) return null;
    const parts = timeStr.split(':');
    return parts.slice(0, 2).join(':'); // Take only hour and minute parts
};

// Hour/minute choices for the working-hours dialog (Persian labels)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return { value: hour, label: toPersianDigits(hour) };
});

const MINUTE_OPTIONS = Array.from({ length: 4 }, (_, i) => {
    const minute = (i * 15).toString().padStart(2, '0');
    return { value: minute, label: toPersianDigits(minute) };
});

// Split a stored Persian 'HH:MM' into English hour/minute parts
const parseTimeParts = (persianTime) => {
    if (!persianTime) return { hour: '', minute: '' };
    const [hour = '', minute = ''] = toEnglishDigits(persianTime).split(':');
    return { hour, minute };
};
import api from '../../../../config/axiosConfig';

// Persian day names
const DAY_NAMES = [
    'شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 
    'چهارشنبه', 'پنج‌شنبه', 'جمعه'
];

// Day choices for API
const DAY_CHOICES = [
    { value: 0, label: 'شنبه' },
    { value: 1, label: 'یکشنبه' },
    { value: 2, label: 'دوشنبه' },
    { value: 3, label: 'سه‌شنبه' },
    { value: 4, label: 'چهارشنبه' },
    { value: 5, label: 'پنج‌شنبه' },
    { value: 6, label: 'جمعه' }
];

export default function TeacherScheduleForm({ teacherId = null, onSaved = null }) {
    // When teacherId is given (edit-drawer mode) the teacher picker is hidden
    const [selectedTeacher, setSelectedTeacher] = useState(teacherId ?? '');
    const [workingDays, setWorkingDays] = useState({});
    const [originalWorkingDays, setOriginalWorkingDays] = useState({});
    const [isEditing, setIsEditing] = useState(true); // Start in editing mode for new schedules
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingChanges, setPendingChanges] = useState([]);
    const [instituteSettings, setInstituteSettings] = useState(null);
    // Single dialog that edits a day's whole working range (start + end)
    const [timeDialog, setTimeDialog] = useState(null);

    // Auto-dismiss messages after 5 seconds
    useEffect(() => {
        if (message.text && message.type === 'success') {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 5000); // Clear message after 5 seconds

            return () => clearTimeout(timer); // Cleanup the timer on unmount or message change
        }
    }, [message]);

    // Fetch teachers for the picker; not needed when the teacher is fixed
    const { data: teachersData, loading: teachersLoading } = useFetch(
        teacherId ? null : '/accounts/api/v1/profile/teachers/?page_size=200'
    );
    const teachers = teachersData?.results || [];

    // Fetch institute settings to get default work hours
    const { data: instituteSettingsData } = useFetch('/institutes/api/v1/teacher-working-days/institute-settings/');
    
    useEffect(() => {
        if (instituteSettingsData) {
            setInstituteSettings(instituteSettingsData);
        }
    }, [instituteSettingsData]);

    // Fetch working days for selected teacher
    const { data: workingDaysData, loading: workingDaysLoading, refetch: refetchWorkingDays } = useFetch(
        selectedTeacher ? `/institutes/api/v1/teacher-working-days/by_teacher/?teacher_id=${selectedTeacher}` : null
    );

    // Initialize working days when teacher is selected
    useEffect(() => {
        if (selectedTeacher) {
            const days = {};
            
            // Get institute default times in Persian digits
            const defaultStartTime = instituteSettings?.work_hours_start 
                ? toPersianDigits(formatTimeWithoutSeconds(instituteSettings.work_hours_start))
                : null;
            const defaultEndTime = instituteSettings?.work_hours_end 
                ? toPersianDigits(formatTimeWithoutSeconds(instituteSettings.work_hours_end))
                : null;
            
            // Initialize all days as not working first, but with institute default times
            for (let i = 0; i < 7; i++) {
                days[i] = {
                    is_working: false,
                    start_time: defaultStartTime,
                    end_time: defaultEndTime
                };
            }
            
            // If there's existing data, update with it
            if (workingDaysData && workingDaysData.length > 0) {
                workingDaysData.forEach(day => {
                    days[day.day] = {
                        is_working: day.is_working,
                        start_time: day.start_time ? toPersianDigits(formatTimeWithoutSeconds(day.start_time)) : defaultStartTime,
                        end_time: day.end_time ? toPersianDigits(formatTimeWithoutSeconds(day.end_time)) : defaultEndTime
                    };
                });
            }
            
            setWorkingDays(days);
            setOriginalWorkingDays(JSON.parse(JSON.stringify(days))); // Deep copy for comparison
        }
    }, [workingDaysData, selectedTeacher, instituteSettings]);

    const handleTeacherChange = (event) => {
        const teacherId = event.target.value;
        setSelectedTeacher(teacherId);
        setIsEditing(true); // Keep in editing mode for new schedule creation
        // Don't reset workingDays here - let useEffect handle it when data loads
    };

    const handleDayChange = (day, field, value) => {
        setWorkingDays(prev => {
            const updatedDays = {
                ...prev,
                [day]: {
                    ...prev[day],
                    [field]: value
                }
            };

            // If the day is being checked (is_working becomes true), set institute default times
            if (field === 'is_working' && value === true && instituteSettings) {
                const defaultStartTime = instituteSettings.work_hours_start 
                    ? toPersianDigits(formatTimeWithoutSeconds(instituteSettings.work_hours_start))
                    : null;
                const defaultEndTime = instituteSettings.work_hours_end 
                    ? toPersianDigits(formatTimeWithoutSeconds(instituteSettings.work_hours_end))
                    : null;

                updatedDays[day] = {
                    ...updatedDays[day],
                    start_time: defaultStartTime,
                    end_time: defaultEndTime
                };
            }

            return updatedDays;
        });
    };

    const openTimeDialog = (day, dayName) => {
        const dayData = workingDays[day] || {};
        setTimeDialog({
            day,
            dayName,
            start: parseTimeParts(dayData.start_time),
            end: parseTimeParts(dayData.end_time)
        });
    };

    const setDialogPart = (edge, part, value) => {
        setTimeDialog(prev => ({ ...prev, [edge]: { ...prev[edge], [part]: value } }));
    };

    const dialogMinutes = (edge) => {
        if (!timeDialog?.[edge]?.hour || !timeDialog?.[edge]?.minute) return null;
        return parseInt(timeDialog[edge].hour, 10) * 60 + parseInt(timeDialog[edge].minute, 10);
    };

    const timeDialogValid = () => {
        const start = dialogMinutes('start');
        const end = dialogMinutes('end');
        return start !== null && end !== null && end > start;
    };

    const handleTimeDialogSave = () => {
        if (!timeDialogValid()) return;
        const { day, start, end } = timeDialog;
        handleDayChange(day, 'start_time', toPersianDigits(`${start.hour}:${start.minute}`));
        handleDayChange(day, 'end_time', toPersianDigits(`${end.hour}:${end.minute}`));
        setTimeDialog(null);
    };

    // Check if there are any changes
    const hasChanges = () => {
        return JSON.stringify(workingDays) !== JSON.stringify(originalWorkingDays);
    };

    // Check if working hours contain all existing student sessions and validate day removal
    const checkForOverlaps = async () => {
        if (!selectedTeacher) return { hasConflicts: false, conflicts: [] };

        try {
            const allConflicts = [];
            const dayNames = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];
            
            // Check each working day for sessions that fall outside working hours
            for (const [day, dayData] of Object.entries(workingDays)) {
                if (dayData.is_working && dayData.start_time && dayData.end_time) {
                    // Convert Persian digits to English for API call
                    const startTime = toEnglishDigits(dayData.start_time);
                    const endTime = toEnglishDigits(dayData.end_time);
                    
                    console.log(`Checking session containment for day ${day}: ${startTime} - ${endTime}`);
                    
                    // Check if all sessions are within working hours
                    const response = await api.get(`/institutes/api/v1/teacher-working-days/check-overlaps/`, {
                        params: {
                            teacher_id: selectedTeacher,
                            day: parseInt(day),
                            start_time: startTime,
                            end_time: endTime
                        }
                    });
                    
                    console.log('Session containment check response:', response.data);
                    
                    if (response.data && response.data.has_conflicts && response.data.conflicts && response.data.conflicts.length > 0) {
                        allConflicts.push({
                            day: parseInt(day),
                            dayName: dayNames[parseInt(day)],
                            time: `${dayData.start_time} - ${dayData.end_time}`,
                            sessions: response.data.conflicts,
                            totalSessions: response.data.total_sessions,
                            type: 'time_conflict'
                        });
                    }
                }
            }
            
            // Check for days being removed that have active sessions
            for (const [day, dayData] of Object.entries(workingDays)) {
                const originalDayData = originalWorkingDays[day];
                
                // If day was working before but is not working now (being removed)
                if (originalDayData && originalDayData.is_working && !dayData.is_working) {
                    console.log(`Checking for active sessions on day ${day} that is being removed`);
                    
                    try {
                        // Check if there are any active sessions on this day
                        const response = await api.get(`/institutes/api/v1/teacher-working-days/check-day-sessions/`, {
                            params: {
                                teacher_id: selectedTeacher,
                                day: parseInt(day)
                            }
                        });
                        
                        console.log('Day removal check response:', response.data);
                        
                        if (response.data && response.data.has_active_sessions && response.data.sessions && response.data.sessions.length > 0) {
                            allConflicts.push({
                                day: parseInt(day),
                                dayName: dayNames[parseInt(day)],
                                sessions: response.data.sessions,
                                type: 'removal_conflict'
                            });
                        }
                    } catch (error) {
                        console.error(`Error checking sessions for day ${day}:`, error);
                        // If we can't check, we'll be conservative and prevent removal
                        allConflicts.push({
                            day: parseInt(day),
                            dayName: dayNames[parseInt(day)],
                            sessions: [],
                            type: 'removal_conflict',
                            error: 'Unable to verify sessions'
                        });
                    }
                }
            }
            
            console.log('Final conflicts result:', { hasConflicts: allConflicts.length > 0, conflicts: allConflicts });
            return { hasConflicts: allConflicts.length > 0, conflicts: allConflicts };
        } catch (error) {
            console.error('Error checking session containment:', error);
            // If API call fails, we'll proceed without checking for now
            return { hasConflicts: false, conflicts: [] };
        }
    };

    // Generate summary of changes
    const getChangesSummary = () => {
        const changes = [];
        const dayNames = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];
        
        Object.keys(workingDays).forEach(day => {
            const current = workingDays[day];
            const original = originalWorkingDays[day];
            const dayName = dayNames[parseInt(day)];
            
            if (!original) {
                // New day
                if (current.is_working) {
                    changes.push(`اضافه کردن ${dayName}: ${current.start_time} - ${current.end_time}`);
                }
            } else if (!current.is_working && original.is_working) {
                // Day removed
                changes.push(`حذف ${dayName}`);
            } else if (current.is_working && !original.is_working) {
                // Day added
                changes.push(`اضافه کردن ${dayName}: ${current.start_time} - ${current.end_time}`);
            } else if (current.is_working && original.is_working) {
                // Day modified
                const timeChanged = current.start_time !== original.start_time || current.end_time !== original.end_time;
                if (timeChanged) {
                    changes.push(`تغییر ${dayName}: ${original.start_time || 'تعریف نشده'} - ${original.end_time || 'تعریف نشده'} → ${current.start_time} - ${current.end_time}`);
                }
            }
        });
        
        return changes;
    };

    const handleSave = async () => {
        if (!selectedTeacher) {
            setMessage({ type: 'error', text: 'لطفاً یک آموزگار انتخاب کنید' });
            return;
        }

        if (!hasChanges()) {
            setMessage({ type: 'info', text: 'هیچ تغییری برای ذخیره وجود ندارد' });
            return;
        }

        // Check if working hours contain all sessions first
        setIsLoading(true);
        const conflictResult = await checkForOverlaps();
        setIsLoading(false);

        console.log('Conflict result in handleSave:', conflictResult);

        if (conflictResult.hasConflicts) {
            console.log('Conflicts detected, showing error message');
            
            // Separate conflicts by type
            const timeConflicts = conflictResult.conflicts.filter(c => c.type === 'time_conflict');
            const removalConflicts = conflictResult.conflicts.filter(c => c.type === 'removal_conflict');
            
            let errorMessage = '';
            
            // Handle time conflicts (sessions outside working hours)
            if (timeConflicts.length > 0) {
                const timeConflictMessages = timeConflicts.map(conflict => {
                    const sessionDetails = conflict.sessions.map(session => 
                        `${session.student_name} (${session.course_name}) ساعت ${session.session_start_time}-${session.session_end_time}`
                    ).join('، ');
                    return `${conflict.dayName} ساعت کاری ${conflict.time}:\n${sessionDetails}`;
                }).join('\n\n');
                
                errorMessage += `ساعات کاری پیشنهادی شامل تمام جلسات موجود نیست:\n\n${timeConflictMessages}`;
            }
            
            // Handle removal conflicts (days with active sessions being removed)
            if (removalConflicts.length > 0) {
                if (errorMessage) errorMessage += '\n\n';
                
                const removalConflictMessages = removalConflicts.map(conflict => {
                    if (conflict.error) {
                        return `${conflict.dayName}: ${conflict.error}`;
                    }
                    const sessionDetails = conflict.sessions.map(session => 
                        `${session.student_name} (${session.course_name}) ساعت ${session.session_start_time}-${session.session_end_time}`
                    ).join('، ');
                    return `${conflict.dayName}:\n${sessionDetails}`;
                }).join('\n\n');
                
                errorMessage += `نمی‌توان روزهای زیر را حذف کرد زیرا دارای جلسات فعال هستند:\n\n${removalConflictMessages}`;
            }
            
            if (timeConflicts.length > 0) {
                errorMessage += '\n\nلطفاً ساعات کاری را به گونه‌ای تنظیم کنید که شامل تمام جلسات موجود باشد.';
            }
            if (removalConflicts.length > 0) {
                errorMessage += '\n\nلطفاً ابتدا جلسات فعال را لغو یا تغییر دهید، سپس روز کاری را حذف کنید.';
            }
            
            console.log('Setting error message:', errorMessage);

            setMessage({ 
                type: 'error', 
                text: errorMessage
            });
            return;
        } else {
            console.log('No conflicts detected, all sessions are within working hours');
        }

        // Show confirmation dialog
        setPendingChanges(getChangesSummary());
        setShowConfirmDialog(true);
    };

    const handleConfirmSave = async () => {
        setIsLoading(true);
        setMessage({ type: '', text: '' });
        setShowConfirmDialog(false);

        try {
            const workingDaysArray = Object.entries(workingDays).map(([day, data]) => ({
                teacher: parseInt(selectedTeacher),
                day: parseInt(day),
                is_working: data.is_working,
                start_time: data.is_working && data.start_time ? formatTimeWithoutSeconds(toEnglishDigits(data.start_time)) : null,
                end_time: data.is_working && data.end_time ? formatTimeWithoutSeconds(toEnglishDigits(data.end_time)) : null
            }));

            const response = await api.post('/institutes/api/v1/teacher-working-days/bulk_update/', {
                working_days: workingDaysArray
            });

            setMessage({ type: 'success', text: 'برنامه کاری آموزگار با موفقیت ثبت شد' });
            // Update original data to reflect saved changes
            setOriginalWorkingDays(JSON.parse(JSON.stringify(workingDays)));
            refetchWorkingDays();
            if (onSaved) onSaved();
        } catch (error) {
            console.error('API Error:', error.response?.data);
            
            // Handle nested DRF validation errors
            let errorMessage = 'خطا در ارتباط با سرور';
            
            if (error.response?.data) {
                const data = error.response.data;
                
                // Handle field-level errors
                if (data.working_days) {
                    const fieldErrors = [];
                    data.working_days.forEach((dayError, index) => {
                        if (dayError) {
                            Object.entries(dayError).forEach(([field, messages]) => {
                                if (Array.isArray(messages)) {
                                    fieldErrors.push(`${field}: ${messages.join(', ')}`);
                                } else {
                                    fieldErrors.push(`${field}: ${messages}`);
                                }
                            });
                        }
                    });
                    if (fieldErrors.length > 0) {
                        errorMessage = `خطاهای اعتبارسنجی: ${fieldErrors.join('; ')}`;
                    }
                } else if (data.detail) {
                    errorMessage = data.detail;
                } else if (data.error) {
                    errorMessage = data.error;
                } else if (typeof data === 'string') {
                    errorMessage = data;
                }
            }
            
            setMessage({ type: 'error', text: errorMessage });
        } finally {
            setIsLoading(false);
        }
    };


    const renderDayRow = (day, dayName) => {
        const dayData = workingDays[day] || { is_working: false, start_time: null, end_time: null };
        
        return (
            <Grid item xs={12} key={day}>
                <Paper sx={{ p: 2, mb: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {dayName}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={dayData.is_working}
                                        onChange={(e) => handleDayChange(day, 'is_working', e.target.checked)}
                                    />
                                }
                                label="روز کاری"
                            />
                        </Grid>
                        
                        {dayData.is_working && (
                            <Grid item xs={12} sm={8}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ScheduleIcon />}
                                    onClick={() => openTimeDialog(day, dayName)}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {dayData.start_time && dayData.end_time
                                        ? `${dayData.start_time} تا ${dayData.end_time}`
                                        : 'تعیین ساعت کاری'}
                                </Button>
                            </Grid>
                        )}
                        
                    </Grid>
                </Paper>
            </Grid>
        );
    };

    return (
        <RTL>
            <Box sx={{ p: 3 }}>

                <FloatingSnackbar
                    open={Boolean(message.text)}
                    message={message.text}
                    severity={message.type || 'info'}
                    onClose={() => setMessage({ type: '', text: '' })}
                    anchor={{ vertical: 'bottom', horizontal: 'left' }}
                    autoHideDuration={5000}
                />

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        {!teacherId && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="انتخاب آموزگار"
                                    value={selectedTeacher}
                                    onChange={handleTeacherChange}
                                >
                                    {teachers.map((teacher) => (
                                        <MenuItem key={teacher.id} value={teacher.user.id}>
                                            {teacher.user.first_name} {teacher.user.last_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={teacherId ? 12 : 6}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    disabled={isLoading || !selectedTeacher || !hasChanges()}
                                >
                                    {isLoading ? <CircularProgress size={20} /> : 'ثبت یا بروزرسانی'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {selectedTeacher && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            انتخاب روزهای کاری و ساعات کار
                        </Typography>
                        
                        {workingDaysLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={1}>
                                {DAY_CHOICES.map(({ value, label }) => 
                                    renderDayRow(value, label)
                                )}
                            </Grid>
                        )}
                    </Paper>
                )}
            </Box>

            {/* Working-range dialog: start and end of one day in a single popup */}
            <Dialog open={Boolean(timeDialog)} onClose={() => setTimeDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {`ساعت کاری ${timeDialog?.dayName || ''}`}
                </DialogTitle>
                <DialogContent>
                    {['start', 'end'].map((edge) => (
                        <Box key={edge} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
                            <Typography sx={{ width: 24, flexShrink: 0, fontWeight: 'bold' }}>
                                {edge === 'start' ? 'از' : 'تا'}
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="ساعت"
                                value={timeDialog?.[edge]?.hour || ''}
                                onChange={(e) => setDialogPart(edge, 'hour', e.target.value)}
                            >
                                {HOUR_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="دقیقه"
                                value={timeDialog?.[edge]?.minute || ''}
                                onChange={(e) => setDialogPart(edge, 'minute', e.target.value)}
                            >
                                {MINUTE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    ))}
                    {dialogMinutes('start') !== null && dialogMinutes('end') !== null && !timeDialogValid() && (
                        <Typography variant="caption" color="error">
                            ساعت پایان باید بعد از ساعت شروع باشد
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTimeDialog(null)}>
                        لغو
                    </Button>
                    <Button onClick={handleTimeDialogSave} variant="contained" disabled={!timeDialogValid()}>
                        تأیید
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    تأیید تغییرات
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        آیا می‌خواهید تغییرات زیر را اعمال کنید؟
                    </Typography>
                    <List dense>
                        {pendingChanges.map((change, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemText 
                                    primary={change}
                                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirmDialog(false)}>
                        لغو
                    </Button>
                    <Button onClick={handleConfirmSave} variant="contained" color="primary">
                        تأیید و ذخیره
                    </Button>
                </DialogActions>
            </Dialog>
        </RTL>
    );
}
